import { MODEL_VERSION, SCENE } from '@/app/constants';
import type { TemplateFormParams } from '@/app/stores/slices/task-form';
import type { TaskInfo, TaskSettings } from '@/app/types';
import { isImageTask } from '@/app/utils';
import {
  DEFAULT_ESTIMATED_TIME,
  DEFAULT_IMG_ESTIMATED_TIME,
  ESTIMATED_TIME,
  IMG_ESTIMATED_TIME,
  MINUTES_IN_DAY,
  MINUTES_IN_HOUR,
  MOTION_CONTROL_BASE_ESTIMATED_TIME,
  MOTION_CONTROL_ESTIMATED_TIME_PER_SECOND,
  ONE_MINUTE,
  TEMPLATE_ESTIMATED_TIME,
} from '../constants';

export * from './button-config';
export * from './claw-pass-badge-class-name';

/**
 * 根据任务类型与配置估计生成任务需要的时间
 *
 * @param type - 任务类型
 * @param taskSettings - 任务相关设置，可能为 null
 * @param scene - 如果是模版，传入是哪种场景
 * @returns 预估时间（单位：秒）
 */
export const getEstimateTime = (
  type: TaskInfo['type'],
  taskSettings: TaskSettings & TaskInfo['settings'],
  scene?: string,
  sceneFormParams?: TemplateFormParams,
) => {
  if (type === SCENE) {
    if (
      scene &&
      sceneFormParams &&
      sceneFormParams.templateScene === scene &&
      sceneFormParams.estimatedDuration !== undefined
    ) {
      return sceneFormParams.estimatedDuration;
    }

    // 模版兜底
    return TEMPLATE_ESTIMATED_TIME;
  }

  const modelVersion = taskSettings?.model_version;

  // short_story 统一返回33分钟
  if (type === 'short_story') {
    return 33 * MINUTES_IN_HOUR;
  }

  if (type === 'motion_control') {
    const duration = Math.max(0, taskSettings?.duration ?? 15);
    return MOTION_CONTROL_BASE_ESTIMATED_TIME + duration * MOTION_CONTROL_ESTIMATED_TIME_PER_SECOND;
  }

  if (isImageTask({ type })) {
    // 生图
    if ((modelVersion === MODEL_VERSION.Q1 || modelVersion === MODEL_VERSION.Q2) && taskSettings?.resolution) {
      const taskTypeKey = isImageTask({ type }) ? type : 'default';
      return IMG_ESTIMATED_TIME[modelVersion]?.[taskTypeKey]?.[taskSettings.resolution] ?? DEFAULT_IMG_ESTIMATED_TIME;
    }
  } else {
    // 生视频
    const duration = taskSettings?.duration ?? 4;

    if ((modelVersion === MODEL_VERSION['2.0'] || modelVersion === MODEL_VERSION.Q1) && taskSettings?.resolution) {
      const taskTypeKey = type === 'character2video' ? 'character2video' : 'default';
      return ESTIMATED_TIME[modelVersion][taskTypeKey][taskSettings.resolution][duration];
    }
    if ((modelVersion === MODEL_VERSION.Q2 || modelVersion === MODEL_VERSION.Q2_PRO) && taskSettings) {
      // 处理 multi_frame 类型：统一返回30分钟（1800秒）
      if (type === 'multi_frame') {
        return 30 * MINUTES_IN_HOUR;
      }

      const taskOptionKey = taskSettings.lip_sync
        ? 'lip_sync'
        : taskSettings.with_audio
          ? 'with_audio'
          : (taskSettings.transition ?? 'default');
      return ESTIMATED_TIME[modelVersion][type]?.[taskOptionKey]?.[duration] ?? DEFAULT_ESTIMATED_TIME;
    }
    if (modelVersion === MODEL_VERSION.Q3 && taskSettings) {
      const transition = taskSettings.transition ?? 'pro';
      return ESTIMATED_TIME[modelVersion][type]?.[transition]?.[duration] ?? DEFAULT_ESTIMATED_TIME;
    }
  }

  // 兜底（通常不会走到这里）
  return DEFAULT_ESTIMATED_TIME;
};

/**
 * 根据接口返回的排队等待时间 minWait 和 maxWait 返回合适的文案展示
 *
 * @param minWait - 当前排队的最小等待时间（单位：分钟）
 * @param maxWait - 当前排队的最大等待时间（单位：分钟）
 * @param isWaitTimeProlonged - 是否需要增加额外提示
 * @returns 包含文案 key 及附加参数的对象
 */
export function getQueueI18nMessageConfig(
  minWait: number,
  maxWait: number,
  isWaitTimeProlonged?: boolean,
): {
  key: 'waitLessThanOneMinute' | 'waitMoreThan' | 'waitTime' | 'waitTimeProlonged' | 'waitMoreThanProlonged';
  params?: {
    min: number;
    max: number;
    unit: 'minutes' | 'hours';
  };
} {
  if (maxWait <= ONE_MINUTE) {
    return { key: 'waitLessThanOneMinute' };
  }
  if (minWait >= MINUTES_IN_DAY) {
    return {
      key: isWaitTimeProlonged ? 'waitMoreThanProlonged' : 'waitMoreThan',
      params: {
        min: 24,
        max: 24,
        unit: 'hours',
      },
    };
  }

  const unit = minWait >= MINUTES_IN_HOUR ? 'hours' : 'minutes';

  return {
    key: isWaitTimeProlonged ? 'waitTimeProlonged' : 'waitTime',
    params: {
      min: Math.floor(minWait / (unit === 'hours' ? MINUTES_IN_HOUR : 1)),
      max: Math.floor(maxWait / (unit === 'hours' ? MINUTES_IN_HOUR : 1)),
      unit,
    },
  };
}
